import {
  type BuyerMaterialSpecificationAdminListItem,
  MaterialComponentCategory,
  type SimplePackagingSpecification,
} from 'api';
import { MaterialComponentValueList } from 'features';
import { formatPackedOn, formatStorageType, formatStrapping } from 'form';
import { useTranslation } from 'i18n';
import { Link } from 'react-router-dom';
import { Box, Button, capitalizeString, ShowMore, Stack, Typography, TypographyIcon } from 'ui';
import { Inventory2Outlined, Notes } from 'ui/icons';

type Props = {
  buyerMaterialSpec: BuyerMaterialSpecificationAdminListItem;
};

const MAX_SUMMARY_LENGTH = 100;

const formatSimplePackagingSpecification = (spec: SimplePackagingSpecification) => {
  if (spec.title) {
    return spec.title;
  }
  if (!spec.packed_on) {
    return `${formatStorageType(spec.storage_type)} with ${spec.strapping.map((strapping) => formatStrapping(strapping)).join(', ')}`;
  }
  return (
    spec.title ||
    `${formatStorageType(spec.storage_type)} on ${formatPackedOn(spec.packed_on)} with ${spec.strapping.map((strapping) => formatStrapping(strapping)).join(', ')}`
  );
};

const extractMaterialPackagingSpecifications = (
  packagingSpecifications: BuyerMaterialSpecificationAdminListItem['packaging_specifications']
): string => packagingSpecifications.map((spec) => formatSimplePackagingSpecification(spec)).join(', ');

const getBuyerMaterialSpecificationTitle = (buyerMaterialSpec: BuyerMaterialSpecificationAdminListItem) => {
  let title = `${buyerMaterialSpec.material_grade.name} ${buyerMaterialSpec.material.title} ${capitalizeString(buyerMaterialSpec.material_form.type)}`;
  if (buyerMaterialSpec.material_colour) {
    title = `${title} ${capitalizeString(buyerMaterialSpec.material_colour.name)}`;
  }
  return title;
};

export const BuyerMaterialSpecificationItem = ({ buyerMaterialSpec }: Props) => {
  const { t } = useTranslation('orders');
  const materialQualities = buyerMaterialSpec.material_component_values.filter(
    (materialComponent) =>
      materialComponent.supported_component.component.category === MaterialComponentCategory.MATERIAL_QUALITY
  );

  const materialPackagingSpecifications = extractMaterialPackagingSpecifications(
    buyerMaterialSpec.packaging_specifications
  );

  return (
    <Box
      sx={{
        backgroundColor: 'background.default',
        borderRadius: 2,
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        justifyContent: 'space-between',
        padding: 2,
      }}
    >
      <Stack spacing={2.5}>
        <Stack
          direction="row"
          sx={{
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <Typography fontWeight="bold">{getBuyerMaterialSpecificationTitle(buyerMaterialSpec)}</Typography>
          {buyerMaterialSpec.file_url ? (
            <Button
              component="a"
              href={buyerMaterialSpec.file_url}
              target="_blank"
              aria-label={t('buyerMaterialSpecifications.item.actions.downloadAriaLabel')}
              variant="secondary"
            >
              {t('buyerMaterialSpecifications.item.actions.downloadSpecFile')}
            </Button>
          ) : null}
        </Stack>
        <MaterialComponentValueList materialComponents={materialQualities} />

        <ShowMore
          hideLabel={t('buyerMaterialSpecifications.item.actions.hide')}
          text={materialPackagingSpecifications || t('buyerMaterialSpecifications.item.values.noneAdded')}
          textMarginLeft={4}
          maxLength={MAX_SUMMARY_LENGTH}
          showMoreLabel={t('buyerMaterialSpecifications.item.actions.showMore')}
          label={
            <TypographyIcon icon={Inventory2Outlined}>
              {t('buyerMaterialSpecifications.item.labels.packagingSpecifications')}
            </TypographyIcon>
          }
        />

        <ShowMore
          hideLabel={t('buyerMaterialSpecifications.item.actions.hide')}
          text={buyerMaterialSpec.notes || t('buyerMaterialSpecifications.item.values.noneAdded')}
          textMarginLeft={4}
          maxLength={MAX_SUMMARY_LENGTH}
          showMoreLabel={t('buyerMaterialSpecifications.item.actions.showMore')}
          label={<TypographyIcon icon={Notes}>{t('buyerMaterialSpecifications.item.labels.notes')}</TypographyIcon>}
        />
      </Stack>
      <Box sx={{ mt: 'auto', pt: 2 }}>
        <Button fullWidth variant="secondary" component={Link} to={buyerMaterialSpec.id.toString()}>
          {t('buyerMaterialSpecifications.item.actions.edit')}
        </Button>
      </Box>
    </Box>
  );
};
